import { call, put, takeEvery, takeLeading } from 'redux-saga/effects';
import {
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_INIT,
  LOGOUT_REQUEST,
  LOGOUT_SUCCESS,
  FB_LOGIN_REQUEST,
  APPLE_LOGIN_REQUEST,
  SIGNUP_REQUEST,
  SIGNUP_SUCCESS,
  SIGNUP_FAILURE,
  LOGIN_FAILURE,
  AUTO_LOGIN_REQUEST,
  AUTO_LOGIN_SUCCESS,
  LOGIN_INIT_SUCCESS,
  AUTO_LOGIN_FAILURE,
} from '../../constants/login';
import {
  loginActions,
  logoutRequestAction,
  loginInitSuccessAction,
} from '../../store/login/actions';
import {
  fbLoginRequest,
  appleLoginRequest,
  loginRequest,
  logoutRequest,
  signupRequest,
  autoLoginRequest,
} from '../../api/login';

/* Types */
import { Credentials, SignupResponse } from '../../types/Login';
import { PUBLIC_ROUTES } from '../../utils/constants';
import { isSignupCredentialsError } from './authErrors';

/* Utils */
import { push } from 'connected-react-router';
import { getUser, storeUser, USER_STORAGE_USER_KEY } from './utils';
import { getApiAuthorization, setUpAxiostoken } from '../../api/utils';
import { toast } from 'react-toastify';
import { get } from 'lodash';

/* Translations */
import i18n from '../../i18n';

/* Services */
import { postEvent } from '../../api/tracking';
import { getCurrent } from '../../api/me';

/* Tracking */
import Cookies from 'universal-cookie';

function* loginRequestSaga(action: any): any {
  try {
    const user = yield call(loginRequest, action.payload.credentials);
    const { redirect } = action.payload;
    
    // Check if login was successful
    if (!user || !user.access_token) {
      yield put(loginActions.loginFailureAction(new Error('login_failed')));
      return;
    }

    // Login successful - continue
    yield setUpAxiostoken(user.access_token.value);
    yield put(loginActions.loginSuccessAction(user, redirect));
  } catch (e: any) {
    yield put(loginActions.loginFailureAction(e));
  }
}

export function* signupRequestSaga(action: any): any {
  try {
    const { email, password, visitorID } = action.payload;
    const user: SignupResponse = yield call(
      signupRequest,
      email,
      password,
      visitorID,
    );
    if (!user || !user.access_token) {
      throw new Error(get(user, 'detail') || 'signup_failed');
    }
    yield setUpAxiostoken(user.access_token.value);
    // Signing up with an existing email and its password logs into that
    // account (TC-1523). A missing `is_new_user` means an older backend.
    if (user.is_new_user === false) {
      yield put(loginActions.loginSuccessAction(user));
    } else {
      yield put(loginActions.signupSuccessAction(user));
    }
  } catch (e: any) {
    // Credential errors are rendered inline by the signup forms, with the
    // login copy, instead of Go's detail ("email already exists").
    if (!isSignupCredentialsError(e?.type)) {
      toast.error(e.detail || e.message);
    }
    yield put(loginActions.signupFailureAction(e));
  }
}
function* fbLoginRequestSaga(action: any) {
  const token = get(action, ['payload', 'accessToken']);
  try {
    const user: Credentials = yield call(fbLoginRequest, token);
    yield setUpAxiostoken(user.access_token.value);
    yield put(loginActions.loginSuccessAction(user));
  } catch (e: any) {
    toast.error(i18n.t('translation:login_error_wrong-pwd'));
    yield put(loginActions.loginFailureAction(e));
  }
}

function* appleLoginRequestSaga(action: any) {
  const token = get(action, ['payload', 'accessToken']);
  const email = get(action, ['payload', 'email']);
  const username = get(action, ['payload', 'username']);
  if (email && username) {
    try {
      const user: Credentials = yield call(
        appleLoginRequest,
        token,
        email,
        username,
      );
      yield setUpAxiostoken(user.access_token.value);
      yield put(loginActions.loginSuccessAction(user));
    } catch (e: any) {
      toast.error(i18n.t('translation:login_error_wrong-pwd'));
      yield put(loginActions.loginFailureAction(e));
    }
  } else
    try {
      const user: Credentials = yield call(appleLoginRequest, token);
      yield setUpAxiostoken(user.access_token.value);
      yield put(loginActions.loginSuccessAction(user));
    } catch (e: any) {
      toast.error(i18n.t('translation:login_error_wrong-pwd'));
      yield put(loginActions.loginFailureAction(e));
    }
}

function* signupSuccessSaga(loginData: any) {
  const userData = get(loginData, ['payload', 'userData']);
  const cookies = new Cookies();
  const areCookiesAccepted = Boolean(cookies.get('cookies_accepted'));
  const pathName = window.location.pathname.split('/');
  let tipID: string = '';
  if (pathName.includes('tips')) {
    tipID = pathName[pathName.length - 1];
  }

  // Set user language based on country_code
  const countryCode = get(userData, ['user', 'country_code']);
  if (countryCode) {
    const languageMap: Record<string, string> = {
      'ES': 'es',
      'MX': 'es-MX',
      'BR': 'pt-BR',
      'PT': 'pt',
      'US': 'en',
      'GB': 'en',
    };
    const language = languageMap[countryCode] || 'en';
    i18n.changeLanguage(language);
  }

  postEvent('WEB_SIGNUP', userData.user.id, {
    tip_id: tipID,
    additional_info: areCookiesAccepted ? cookies.get('visitor_id') : '',
  });

  cookies.remove('visitor_id');

  yield call(storeUser, loginData.payload.userData);
}

function* signupFailureSaga(loginData: any) {
  const cookies = new Cookies();
  const areCookiesAccepted = Boolean(cookies.get('cookies_accepted'));
  const tipID = window.location.pathname.split('/');
  const visitorID = cookies.get('visitor_id');
  yield areCookiesAccepted &&
    postEvent('FAILED_WEB_SIGNUP', visitorID, {
      tip_id: tipID[tipID.length - 1],
      additional_info: `${cookies.get('visitor_id')}/error`,
    });
}

function* loginSuccessSaga(action: any) {
  const { redirect } = action.payload;
  const userData = get(action, ['payload', 'userData']);
  const cookies = new Cookies();
  const areCookiesAccepted = Boolean(cookies.get('cookies_accepted'));
  const tipID = window.location.pathname.split('/');

  // Set user language based on country_code
  const countryCode = get(userData, ['user', 'country_code']);
  if (countryCode) {
    const languageMap: Record<string, string> = {
      'ES': 'es',
      'MX': 'es-MX',
      'BR': 'pt-BR',
      'PT': 'pt',
      'US': 'en',
      'GB': 'en',
    };
    const language = languageMap[countryCode] || 'en';
    i18n.changeLanguage(language);
  }

  postEvent('WEB_LOGIN', userData.user.id, {
    tip_id: tipID[tipID.length - 1],
    additional_info: areCookiesAccepted ? cookies.get('visitor_id') : '',
  });
  cookies.remove('visitor_id');
  yield call(storeUser, action.payload.userData);
  const isTipster = get(action, ['payload', 'userData', 'user', 'is_tipster']);
  
  // Use window.location instead of push() to avoid race conditions with Redux state
  if (redirect) {
    window.location.href = redirect;
  } else if (
    isTipster &&
    (window.location.pathname.includes('/login') ||
      window.location.pathname.includes('/signup'))
  ) {
    window.location.href = '/';
  } else {
    return;
  }
}
function* loginInitSuccessSaga(loginData: any) {
  const userData = get(loginData, ['payload', 'userData']);
  const cookies = new Cookies();
  const areCookiesAccepted = Boolean(cookies.get('cookies_accepted'));
  const pathName = window.location.pathname.split('/');

  let tipID;
  if (pathName.includes('tips')) {
    tipID = pathName[pathName.length - 1];
  }

  // Set user language based on country_code
  const countryCode = get(userData, ['user', 'country_code']);
  if (countryCode) {
    const languageMap: Record<string, string> = {
      'ES': 'es',
      'MX': 'es-MX',
      'BR': 'pt-BR',
      'PT': 'pt',
      'US': 'en',
      'GB': 'en',
    };
    const language = languageMap[countryCode] || 'en';
    i18n.changeLanguage(language);
  }

  postEvent('WEB_LOGIN', userData.user.id, {
    tip_id: tipID,
    additional_info: areCookiesAccepted ? cookies.get('visitor_id') : '',
  });

  cookies.remove('visitor_id');
  yield call(storeUser, loginData.payload.userData);
}

function* loginFailureSaga(loginData: any) {
  const cookies = new Cookies();
  const areCookiesAccepted = Boolean(cookies.get('cookies_accepted'));
  const tipID = window.location.pathname.split('/');
  const additionalInfo: any = { additional_info: 'Error' };
  if (tipID.includes('tips'))
    additionalInfo['tip_id'] = tipID[tipID.length - 1];
  yield areCookiesAccepted &&
    postEvent('FAILED_WEB_LOGIN', cookies.get('visitor_id'), additionalInfo);
  
  // Prevent infinite loop by stopping propagation after login failure
  // The error toast is already shown in loginRequestSaga
}

function* loginInitSaga(): any {
  try {
    const token = yield call(getUser);
    const user = yield call(getCurrent);

    yield put(loginInitSuccessAction({ access_token: token, user: user.user }));
  } catch (e: any) {
    yield put(loginActions.loginInitFailureAction(e));
    const token = getApiAuthorization();
    if (token) {
      yield put(logoutRequestAction());
    }
    const isPublic = PUBLIC_ROUTES.some((element) => {
      if (window.location.pathname.includes(element)) {
        return true;
      }
      return false;
    });
    if (isPublic) {
      return;
    } else yield put(push('/login'));

    return;
  }
}

function* logoutRequestSaga() {
  try {
    yield call(logoutRequest);
    yield put(loginActions.logoutSuccessAction());

    yield window.location.reload();
  } catch (e: any) {
    yield put(loginActions.logoutFailureAction());
  }
}

function* logoutSuccessSaga() {
  try {
    window.localStorage.removeItem(USER_STORAGE_USER_KEY);
    return;
  } catch (e: any) {
    yield put(loginActions.logoutFailureAction());
  }
}

function* autoLoginRequestSaga(action: any): any {
  try {
    let user = yield call(autoLoginRequest, action.payload.token);
    user.access_token = { value: user.access_token };
    const { redirect } = action.payload;
    if (!user.access_token) {
      const error = user;
      const detail = get(error, 'detail');
      new Error(detail);
    }
    yield setUpAxiostoken(user.access_token.value);
    yield put(loginActions.autoLoginSuccessAction(user, redirect));
  } catch (e: any) {
    const { redirect } = action.payload;
    console.error('Full error:', e);
    toast.error('Error: autologin failed');
    yield put(loginActions.autoLoginFailureAction(e, redirect));
  }
}

function* autoLoginSuccessSaga(loginData: any) {
  const userData = get(loginData, ['payload', 'userData']);
  const { redirect } = loginData.payload;
  const cookies = new Cookies();
  const areCookiesAccepted = Boolean(cookies.get('cookies_accepted'));
  const tipID = window.location.pathname.split('/');

  // Set user language based on country_code
  const countryCode = get(userData, ['user', 'country_code']);
  if (countryCode) {
    const languageMap: Record<string, string> = {
      'ES': 'es',
      'MX': 'es-MX',
      'BR': 'pt-BR',
      'PT': 'pt',
      'US': 'en',
      'GB': 'en',
    };
    const language = languageMap[countryCode] || 'en';
    i18n.changeLanguage(language);
  }

  postEvent('WEB_LOGIN', userData.user.id, {
    tip_id: tipID[tipID.length - 1],
    additional_info: areCookiesAccepted ? cookies.get('visitor_id') : '',
  });
  cookies.remove('visitor_id');

  yield call(storeUser, userData);
  yield put(push(redirect));
}
function* autoLoginFailureSaga(action: any) {
  const { redirect } = action.payload;
  const cookies = new Cookies();
  const areCookiesAccepted = Boolean(cookies.get('cookies_accepted'));
  const tipID = window.location.pathname.split('/');
  const additionalInfo: any = { additional_info: 'Error' };
  if (tipID.includes('tips'))
    additionalInfo['tip_id'] = tipID[tipID.length - 1];
  areCookiesAccepted &&
    postEvent('FAILED_WEB_LOGIN', cookies.get('visitor_id'), additionalInfo);
  yield put(push(`/login/sso?redirect=${redirect}`));
}
function* loginSaga() {
  yield takeEvery(LOGIN_REQUEST, loginRequestSaga);
  // Ignore repeated submits while a signup is in flight.
  yield takeLeading(SIGNUP_REQUEST, signupRequestSaga);
  yield takeEvery(FB_LOGIN_REQUEST, fbLoginRequestSaga);
  yield takeEvery(APPLE_LOGIN_REQUEST, appleLoginRequestSaga);
  yield takeEvery(LOGIN_SUCCESS, loginSuccessSaga);
  yield takeEvery(LOGIN_INIT_SUCCESS, loginInitSuccessSaga);
  yield takeEvery(LOGIN_FAILURE, loginFailureSaga);
  yield takeEvery(SIGNUP_SUCCESS, signupSuccessSaga);
  yield takeEvery(SIGNUP_FAILURE, signupFailureSaga);
  yield takeEvery(LOGIN_INIT, loginInitSaga);
  yield takeEvery(LOGOUT_REQUEST, logoutRequestSaga);
  yield takeEvery(LOGOUT_SUCCESS, logoutSuccessSaga);
  yield takeEvery(AUTO_LOGIN_REQUEST, autoLoginRequestSaga);
  yield takeEvery(AUTO_LOGIN_SUCCESS, autoLoginSuccessSaga);
  yield takeEvery(AUTO_LOGIN_FAILURE, autoLoginFailureSaga);
}

export default loginSaga;
